import { Component, ElementRef, Input, Output, EventEmitter, OnChanges, OnInit, OnDestroy, SimpleChanges, HostListener } from '@angular/core';
import { GridsterConfig, GridsterItem, GridsterItemComponentInterface, GridType, DisplayGrid, CompactType } from 'angular-gridster2';
import {
  Widget,
  WidgetEntryData,
  WidgetEntryWithData,
  WidgetWithData,
  WidgetSensorCategory,
} from '@models/widget/widget.model';
import { WIDGET_NUMBER_DEFAULT_COLOR, WIDGET_LIST_DEFAULT_COLOR, WIDGET_SERIES_DEFAULT_COLORS } from '@models/widget/widget.constants';
import { buildPreviewEntries } from '@shared/components/dashboard-widgets/widget-preview-data';

export interface DashboardGridItem extends GridsterItem {
  widget: Widget;
}

interface DashboardScrollSnapshot {
  windowX:       number;
  windowY:       number;
  documentTop:   number;
  bodyTop:       number;
  ionContentTop: number | null;
  ionInnerTop:   number | null;
  gridsterTop:   number | null;
  gridsterLeft:  number | null;
}

interface DashboardScrollLockSnapshot {
  ionContentScrollY: boolean | null;
  ionInnerOverflowY: string | null;
  gridsterOverflowX: string | null;
  gridsterOverflowY: string | null;
}

interface ScrollableIonContent extends HTMLElement {
  scrollToPoint?: (x: number, y: number, duration?: number) => Promise<void>;
  scrollY?: boolean;
}

@Component({
  standalone: false,
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnChanges, OnInit, OnDestroy {

  @Input() gridItems: DashboardGridItem[] = [];
  @Input() loading     = false;
  @Input() isEditMode  = false;
  @Input() areaName    = '';
  @Input() dashboardId = '';
  @Input() mode: 'admin' | 'user' = 'user';
  /** Permisos que el admin concede al usuario: solo limitan en modo usuario. */
  @Input() canCreate = true;
  @Input() canDelete = true;
  @Input() canMove   = true;
  @Input() widgetDataMap: Record<string, WidgetEntryData[]> = {};
  @Input() sensorCatalog: WidgetSensorCategory[] = [];

  @Output() addWidgetClick    = new EventEmitter<void>();
  @Output() editWidgetClick   = new EventEmitter<DashboardGridItem>();
  @Output() removeWidgetClick = new EventEmitter<DashboardGridItem>();
  @Output() layoutChanged     = new EventEmitter<void>();
  @Output() editModeChange    = new EventEmitter<boolean>();
  @Output() backClick         = new EventEmitter<void>();
  @Output() areaInfoClick     = new EventEmitter<void>();

  private renderableWidgetCache = new Map<string, WidgetWithData>();
  private sensorTypeLabelsCache = new Map<string, string[]>();
  private readonly atmosPreviewColors = ['#0075ff', '#48d57c', '#ffc400'];
  private gridsterRefreshHandle: number | null = null;
  private gridInteractionStopHandle: number | null = null;
  private isGridDragging = false;
  private pendingLayoutChange = false;
  private preservedScrollSnapshot: DashboardScrollSnapshot | null = null;
  private scrollLockSnapshot: DashboardScrollLockSnapshot | null = null;
  private readonly editingExtraRows = 2;
  private readonly editingBaseRows = 2;
  private readonly minItemCols = 1;
  private readonly minItemRows = 1;
  private readonly mobileRowSpan = 2;
  private editingGridMinHeight = 0;
  private gridInteractionMode: 'drag' | 'resize' | null = null;
  private savedPositions = new Map<string, { x: number; y: number; cols: number; rows: number }>();

  cols = 6;

  get allowCreate(): boolean { return this.mode !== 'user' || this.canCreate; }
  get allowDelete(): boolean { return this.mode !== 'user' || this.canDelete; }
  get allowMove():   boolean { return this.mode !== 'user' || this.canMove; }
  get isEmptyState(): boolean { return !this.loading && this.gridItems.length === 0; }

  gridsterOptions: GridsterConfig = {
    gridType:    GridType.VerticalFixed,
    displayGrid: DisplayGrid.None,
    compactType: CompactType.None,
    draggable:   { enabled: true, ignoreContent: true, dragHandleClass: 'widget-drag-handle' },
    resizable:   { enabled: true },
    pushItems:      true,
    setGridSize:    true,
    margin:         16,
    outerMargin:    true,
    // La libreria tiene su propio "modo movil" (por debajo de este ancho
    // calcula la altura como filas/columnas * ancho total del grid, ignorando
    // fixedRowHeight) - lo desactivamos porque la app ya tiene su propia
    // logica responsive (updateCols/getMobileRows). Sin esto, en movil todos
    // los widgets con la misma proporcion filas:columnas (p.ej. 1x1 y 2x2)
    // salian con la misma altura, dejando hueco vacio en los mas pequenos.
    mobileBreakpoint: 0,
    minCols:        6,
    maxCols:        6,
    minRows:        1,
    maxRows:        100,
    fixedRowHeight: 140,
    scrollSensitivity:       0,
    scrollSpeed:             0,
    disableScrollVertical:   true,
    disableScrollHorizontal: true,
    enableBoundaryControl:   true,
    itemChangeCallback: () => {
      this.onGridsterItemChanged();
    },
  };

  readonly TYPE_LABELS: Record<string, string> = {
    LINE:        'Gráfico',
    BAR:         'Gráfico',
    AREA:        'Área',
    NUMBER:      'Última lectura',
    GAUGE:       'Gauge',
    PIE:         'Tarta',
    EXPLORER:    'Explorador',
    DEVICE_LIST: 'Lista',
    MAP:         'Mapa',
    WEATHER:     'Meteo',
  };

  readonly SOURCE_LABELS: Record<string, string> = {
    DEVICE:        'Device / Sensor',
    AREA_PROPERTY: 'Propiedades área',
    BLOCKS:        'Blocks',
    METEO:         'Meteorología',
  };

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    this.updateCols();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateCols();
    // Con retraso: si el resize llega en rafaga (p.ej. arrastrando el panel de
    // DevTools), da tiempo a que el resize-observer interno de gridster asiente
    // antes de forzar nuestro propio recalculo - si no, gridster puede quedarse
    // con un ancho fijo (en px) medido a mitad del arrastre.
    this.scheduleGridsterRefresh(150);
  }

  @HostListener('window:focus')
  onWindowFocus(): void {
    this.scheduleGridsterRefresh(80);
  }

  refreshLayout(delayMs = 0): void {
    this.scheduleGridsterRefresh(delayMs);
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (!document.hidden) {
      this.scheduleGridsterRefresh(80);
    }
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    this.closeGridInteractionFromDocument();
  }

  @HostListener('window:mouseup')
  onWindowMouseUp(): void {
    this.closeGridInteractionFromDocument();
  }

  @HostListener('document:pointerup')
  onDocumentPointerUp(): void {
    this.closeGridInteractionFromDocument();
  }

  @HostListener('window:pointerup')
  onWindowPointerUp(): void {
    this.closeGridInteractionFromDocument();
  }

  @HostListener('document:touchend')
  onDocumentTouchEnd(): void {
    this.closeGridInteractionFromDocument();
  }

  @HostListener('document:touchcancel')
  onDocumentTouchCancel(): void {
    this.closeGridInteractionFromDocument();
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    this.closeGridInteractionFromDocument();
  }

  ngOnDestroy(): void {
    if (this.gridsterRefreshHandle !== null) {
      window.clearTimeout(this.gridsterRefreshHandle);
    }
    if (this.gridInteractionStopHandle !== null) {
      window.clearTimeout(this.gridInteractionStopHandle);
    }
    this.unlockScrollContainers();
    this.clearGridShellMinHeight();
  }

  private updateCols(): void {
    const width = window.innerWidth;
    const cols = width > 1200 ? 6 : width > 768 ? 4 : 2;
    if (cols === this.cols) return;
    this.cols = cols;
    this.clearGridShellMinHeight();
    // En ≤600px el header no tiene margin-inline, así que eliminamos el outer margin
    // lateral de gridster para que las tarjetas arranquen al mismo nivel que el header.
    const sideOuter = width <= 600 ? 0 : null;
    this.gridsterOptions = {
      ...this.gridsterOptions,
      minCols: cols,
      maxCols: cols,
      outerMarginLeft: sideOuter,
      outerMarginRight: sideOuter,
    };
    this.normalizeGridItems(true);
    this.scheduleGridsterRefresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gridItems']) {
      this.capturePositions();
      this.normalizeGridItems();
    }
    if (changes['gridItems'] || changes['widgetDataMap']) {
      this.rebuildRenderableWidgetCache();
    }
    if (changes['gridItems'] || changes['sensorCatalog']) {
      this.rebuildSensorTypeLabelsCache();
    }
    if (changes['isEditMode'] || (changes['gridItems'] && this.isEditMode)) {
      this.applyGridsterEditMode(this.isEditMode);
    }
    if (changes['canMove'] || changes['mode']) {
      this.applyMovePermission();
    }
    if (changes['loading'] && !this.loading) {
      this.scheduleGridsterRefresh();
    }
    if (changes['gridItems'] && !changes['gridItems'].firstChange) {
      this.scheduleGridsterRefresh();
    }
  }

  /** Sin permiso de mover, el grid no deja arrastrar ni redimensionar. */
  private applyMovePermission(): void {
    this.gridsterOptions = {
      ...this.gridsterOptions,
      draggable: { ...this.gridsterOptions.draggable, enabled: this.allowMove },
      resizable: { ...this.gridsterOptions.resizable, enabled: this.allowMove },
    };
    this.scheduleGridsterRefresh();
  }

  private applyGridsterEditMode(enabled: boolean): void {
    const enteringEditMode = enabled && !this.gridsterOptions.draggable?.enabled;
    if (!enabled) {
      this.clearGridShellMinHeight();
    }

    this.gridsterOptions = {
      ...this.gridsterOptions,
      draggable: {
        enabled: this.allowMove,
        ignoreContent: true,
        dragHandleClass: 'widget-drag-handle',
        start: (_item: GridsterItem, _itemComponent: GridsterItemComponentInterface, _event: MouseEvent) => {
          this.onGridInteractionStart('drag');
        },
        stop: (_item: GridsterItem, _itemComponent: GridsterItemComponentInterface, _event: MouseEvent) => {
          this.onGridInteractionStop();
        },
      },
      resizable: {
        enabled: this.allowMove,
        start: (_item: GridsterItem, _itemComponent: GridsterItemComponentInterface, _event: MouseEvent) => {
          this.onGridInteractionStart('resize');
        },
        stop: (_item: GridsterItem, _itemComponent: GridsterItemComponentInterface, _event: MouseEvent) => {
          this.onGridInteractionStop();
        },
      },
      minRows: enabled ? this.getStableEditingMinRows(enteringEditMode) : 1,
    };
    this.scheduleGridsterRefresh();
  }

  private onGridInteractionStart(mode: 'drag' | 'resize'): void {
    this.isGridDragging = true;
    this.gridInteractionMode = mode;
    this.pendingLayoutChange = false;
    if (mode === 'drag') {
      this.stabilizeGridShellHeight();
      this.preserveScrollPosition(true);
      this.lockScrollContainers();
    }
    this.applyInteractionRows();
  }

  private onGridInteractionStop(): void {
    if (!this.isGridDragging || this.gridInteractionStopHandle !== null) return;
    const interactionMode = this.gridInteractionMode;
    this.gridInteractionStopHandle = window.setTimeout(() => {
      window.setTimeout(() => {
        this.gridInteractionStopHandle = null;
        this.finishGridInteractionStop(interactionMode);
      }, 0);
    }, 0);
  }

  private finishGridInteractionStop(interactionMode: 'drag' | 'resize' | null): void {
    this.isGridDragging = false;
    this.gridInteractionMode = null;
    this.clearGridsterInteractionArtifacts();
    if (this.isEditMode) {
      this.refreshGridsterEditRows();
    } else {
      this.gridsterOptions = {
        ...this.gridsterOptions,
        minRows: 1,
      };
    }
    if (this.pendingLayoutChange) {
      this.pendingLayoutChange = false;
      this.normalizeGridItems();
      this.capturePositions();
      if (this.allowMove) this.layoutChanged.emit();
    }
    if (interactionMode === 'drag') {
      this.restoreScrollPosition();
    }
    window.setTimeout(() => {
      this.clearGridsterInteractionArtifacts();
      this.unlockScrollContainers();
      this.clearGridShellMinHeight();
    }, 120);
  }

  private closeGridInteractionFromDocument(): void {
    if (!this.isGridDragging) return;
    this.onGridInteractionStop();
  }

  private onGridsterItemChanged(): void {
    if (this.isGridDragging) {
      this.pendingLayoutChange = true;
      return;
    }

    this.refreshGridsterEditRows();
    this.normalizeGridItems();
    if (this.allowMove) this.layoutChanged.emit();
    this.restoreScrollPosition();
  }

  private refreshGridsterEditRows(): void {
    if (!this.showGridBackdrop) return;
    this.gridsterOptions = {
      ...this.gridsterOptions,
      minRows: this.getStableEditingMinRows(),
    };
    this.scheduleGridsterRefresh();
  }

  private applyInteractionRows(): void {
    this.gridsterOptions = {
      ...this.gridsterOptions,
      minRows: this.getStableEditingMinRows(),
    };
    this.gridsterOptions.api?.optionsChanged?.();
    this.gridsterOptions.api?.resize?.();
  }

  private scheduleGridsterRefresh(delayMs = 0): void {
    if (this.gridsterRefreshHandle !== null) {
      window.clearTimeout(this.gridsterRefreshHandle);
    }
    this.preserveScrollPosition();

    this.gridsterRefreshHandle = window.setTimeout(() => {
      this.gridsterRefreshHandle = null;
      this.gridsterOptions.api?.optionsChanged?.();
      this.gridsterOptions.api?.resize?.();
      this.restoreScrollPosition();
    }, delayMs);
  }

  private preserveScrollPosition(force = false): void {
    if (this.preservedScrollSnapshot && !force) return;
    this.preservedScrollSnapshot = this.captureScrollSnapshot();
  }

  private restoreScrollPosition(keepSnapshot = false): void {
    if (!this.preservedScrollSnapshot) return;
    const snapshot = this.preservedScrollSnapshot;
    window.requestAnimationFrame(() => {
      this.applyScrollSnapshot(snapshot);
      window.requestAnimationFrame(() => {
        this.applyScrollSnapshot(snapshot);
        if (!keepSnapshot) {
          this.preservedScrollSnapshot = null;
        }
      });
    });
  }

  private captureScrollSnapshot(): DashboardScrollSnapshot {
    const ionContent = this.getIonContentElement();
    const ionInner = this.getIonInnerScrollElement();
    const gridster = this.getGridsterElement();

    return {
      windowX:       window.scrollX,
      windowY:       window.scrollY,
      documentTop:   document.documentElement.scrollTop,
      bodyTop:       document.body.scrollTop,
      ionContentTop: ionContent?.scrollTop ?? null,
      ionInnerTop:   ionInner?.scrollTop ?? null,
      gridsterTop:   gridster?.scrollTop ?? null,
      gridsterLeft:  gridster?.scrollLeft ?? null,
    };
  }

  private applyScrollSnapshot(snapshot: DashboardScrollSnapshot): void {
    window.scrollTo({ top: snapshot.windowY, left: snapshot.windowX });
    document.documentElement.scrollTop = snapshot.documentTop;
    document.body.scrollTop = snapshot.bodyTop;

    const ionContent = this.getIonContentElement();
    if (ionContent && snapshot.ionContentTop !== null) {
      ionContent.scrollTop = snapshot.ionContentTop;
      void ionContent.scrollToPoint?.(0, snapshot.ionContentTop, 0);
    }

    const ionInner = this.getIonInnerScrollElement();
    if (ionInner && snapshot.ionInnerTop !== null) {
      ionInner.scrollTop = snapshot.ionInnerTop;
    }

    const gridster = this.getGridsterElement();
    if (gridster) {
      if (snapshot.gridsterTop !== null) gridster.scrollTop = snapshot.gridsterTop;
      if (snapshot.gridsterLeft !== null) gridster.scrollLeft = snapshot.gridsterLeft;
    }
  }

  private getIonContentElement(): ScrollableIonContent | null {
    return this.host.nativeElement.closest('ion-content') as ScrollableIonContent | null
      ?? document.querySelector('ion-content.ion-page') as ScrollableIonContent | null
      ?? document.querySelector('ion-content') as ScrollableIonContent | null;
  }

  private getIonInnerScrollElement(): HTMLElement | null {
    return this.getIonContentElement()?.shadowRoot?.querySelector('.inner-scroll') as HTMLElement | null;
  }

  private getGridsterElement(): HTMLElement | null {
    return this.host.nativeElement.querySelector('gridster.db-gridster') as HTMLElement | null;
  }

  private getGridShellElement(): HTMLElement | null {
    return this.host.nativeElement.querySelector('.db-grid-shell') as HTMLElement | null;
  }

  get showGridBackdrop(): boolean {
    return this.isGridDragging;
  }

  private stabilizeGridShellHeight(): void {
    if (!this.showGridBackdrop) return;

    const shell = this.getGridShellElement();
    if (!shell) return;

    const gridster = this.getGridsterElement();
    const currentHeight = Math.ceil(Math.max(
      shell.offsetHeight,
      gridster?.offsetHeight ?? 0,
      this.editingGridMinHeight,
    ));

    if (currentHeight <= 0) return;

    this.editingGridMinHeight = currentHeight;
    shell.style.minHeight = `${currentHeight}px`;
  }

  private clearGridShellMinHeight(): void {
    this.editingGridMinHeight = 0;
    const shell = this.getGridShellElement();
    if (shell) {
      shell.style.minHeight = '';
    }
  }

  private clearGridsterInteractionArtifacts(): void {
    const gridster = this.getGridsterElement();
    if (!gridster) return;

    gridster.classList.remove('display-grid');
    gridster
      .querySelectorAll('gridster-item.gridster-item-moving, gridster-item.gridster-item-resizing, gridster-item.moving, gridster-item.resizing')
      .forEach((item) => {
        item.classList.remove('gridster-item-moving', 'gridster-item-resizing', 'moving', 'resizing');
      });
  }

  private lockScrollContainers(): void {
    if (this.scrollLockSnapshot) return;

    const ionContent = this.getIonContentElement();
    const ionInner = this.getIonInnerScrollElement();
    const gridster = this.getGridsterElement();

    this.scrollLockSnapshot = {
      ionContentScrollY: ionContent?.scrollY ?? null,
      ionInnerOverflowY: ionInner?.style.overflowY ?? null,
      gridsterOverflowX: gridster?.style.overflowX ?? null,
      gridsterOverflowY: gridster?.style.overflowY ?? null,
    };

    if (ionContent) {
      ionContent.scrollY = false;
    }
    if (ionInner) {
      ionInner.style.overflowY = 'hidden';
    }
    if (gridster) {
      gridster.style.overflowX = 'hidden';
      gridster.style.overflowY = 'hidden';
    }
  }

  private unlockScrollContainers(): void {
    if (!this.scrollLockSnapshot) return;

    const snapshot = this.scrollLockSnapshot;
    const ionContent = this.getIonContentElement();
    const ionInner = this.getIonInnerScrollElement();
    const gridster = this.getGridsterElement();

    if (ionContent && snapshot.ionContentScrollY !== null) {
      ionContent.scrollY = snapshot.ionContentScrollY;
    }
    if (ionInner && snapshot.ionInnerOverflowY !== null) {
      ionInner.style.overflowY = snapshot.ionInnerOverflowY;
    }
    if (gridster && snapshot.gridsterOverflowX !== null) {
      gridster.style.overflowX = snapshot.gridsterOverflowX;
    }
    if (gridster && snapshot.gridsterOverflowY !== null) {
      gridster.style.overflowY = snapshot.gridsterOverflowY;
    }

    this.scrollLockSnapshot = null;
  }

  private getEditingMinRows(): number {
    return Math.max(this.getLastOccupiedRow() + this.editingExtraRows, this.editingBaseRows);
  }

  private getStableEditingMinRows(reset = false): number {
    return this.getEditingMinRows();
  }

  private getLastOccupiedRow(): number {
    return this.gridItems.reduce((max, item) => {
      const y = Number(item.y ?? 0);
      const rows = Number(item.rows ?? 1);
      return Math.max(max, y + rows);
    }, 0);
  }

  private capturePositions(): void {
    const ids = new Set(this.gridItems.map(i => i.widget.id));
    for (const key of this.savedPositions.keys()) {
      if (!ids.has(key)) this.savedPositions.delete(key);
    }
    for (const item of this.gridItems) {
      this.savedPositions.set(item.widget.id, {
        x:    Number(item.x    ?? 0),
        y:    Number(item.y    ?? 0),
        cols: Number(item.cols ?? 1),
        rows: Number(item.rows ?? 1),
      });
    }
  }

  private getMobileRows(item: DashboardGridItem): number {
    if (this.hasNoEntries(item) || this.hasNoDevice(item) || this.hasNoSyncedDevices(item)) return 1;
    if (item.widget.widgetType === 'NUMBER' || item.widget.widgetType === 'GAUGE') return 1;
    return this.mobileRowSpan;
  }

  private normalizeGridItems(restoreFromSaved = false): boolean {
    if (restoreFromSaved) {
      for (const item of this.gridItems) {
        const saved = this.savedPositions.get(item.widget.id);
        if (saved) { item.x = saved.x; item.y = saved.y; item.cols = saved.cols; item.rows = saved.rows; }
      }
    }

    let changed = false;
    const maxCols = Math.max(this.cols, this.minItemCols);
    const maxRows = this.gridsterOptions.maxRows ?? 100;

    // Procesar en orden visual (arriba-izquierda primero) para resolver colisiones determinísticamente
    const sorted = [...this.gridItems].sort((a, b) => {
      const dy = Number(a.y ?? 0) - Number(b.y ?? 0);
      return dy !== 0 ? dy : (Number(a.x ?? 0) - Number(b.x ?? 0));
    });

    const placed: Array<{ x: number; y: number; cols: number; rows: number }> = [];

    for (const item of sorted) {
      const cols = this.clampInteger(item.cols, this.minItemCols, maxCols, this.minItemCols);
      const rows = maxCols <= 2
        ? this.getMobileRows(item)
        : this.clampInteger(item.rows, this.minItemRows, maxRows, this.minItemRows);
      // En móvil siempre alineamos a la izquierda; en pantallas más anchas respetamos la posición original
      let x = maxCols <= 2 ? 0 : this.clampInteger(item.x, 0, Math.max(0, maxCols - cols), 0);
      let y = this.clampInteger(item.y, 0, Math.max(0, maxRows - rows), 0);

      // Empujar hacia abajo hasta encontrar posición sin colisión
      while (placed.some(p =>
        x < p.x + p.cols && x + cols > p.x &&
        y < p.y + p.rows && y + rows > p.y
      )) {
        y++;
      }

      if (item.x !== x || item.y !== y || item.cols !== cols || item.rows !== rows) {
        changed = true;
        item.x = x;
        item.y = y;
        item.cols = cols;
        item.rows = rows;
      }

      placed.push({ x, y, cols, rows });
    }

    return changed;
  }

  private clampInteger(value: unknown, min: number, max: number, fallback: number): number {
    const numericValue = Number(value);
    const integerValue = Number.isFinite(numericValue) ? Math.trunc(numericValue) : fallback;
    return Math.min(max, Math.max(min, integerValue));
  }

  private buildRenderable(widget: Widget): WidgetWithData {
    if (this.mode === 'user') {
      const realData   = this.widgetDataMap[widget.id] ?? [];
      const dtoEntries = widget.entries ?? [];

      if (dtoEntries.length > 0) {
        return {
          ...widget,
          entries: dtoEntries.map(entry => {
            const found = realData.find(d => d.entryId === entry.id);
            return { ...entry, data: found?.data ?? [] };
          }) as WidgetEntryWithData[],
        };
      }

      // Fallback: si el DTO no incluye entries (respuesta inesperada), construir desde widgetDataMap sin colores
      return {
        ...widget,
        entries: realData.map(d => ({
          id:             d.entryId,
          dataSourceType: 'DEVICE' as const,
          label:          d.label ?? null,
          data:           d.data,
        })) as unknown as WidgetEntryWithData[],
      };
    }
    return {
      ...widget,
      entries: this.buildAdminPreviewEntries(widget),
    };
  }

  private buildAdminPreviewEntries(widget: Widget): WidgetEntryWithData[] {
    const sourceEntries = widget.entries ?? [];
    const mockCount     = this.getMockEntryCount(widget);
    if (mockCount === 0) return [];

    if (widget.widgetType === 'NUMBER' || widget.widgetType === 'GAUGE') {
      const base = sourceEntries.length
        ? sourceEntries.slice(0, 1)
        : [{ color: WIDGET_NUMBER_DEFAULT_COLOR, label: '', unit: '' }];
      const preview = buildPreviewEntries(base.map((e: any) => ({
        color: this.isDefaultNumberPreviewColor(e.color) ? WIDGET_NUMBER_DEFAULT_COLOR : (e.color ?? undefined),
        label: e.label || e.fieldName || '',
        unit:  e.unit  ?? '',
      })), widget.widgetType);
      return base.map((entry: any, i) => ({
        ...entry,
        color: preview[i]?.color ?? WIDGET_NUMBER_DEFAULT_COLOR,
        label: preview[i]?.label ?? (entry.label || entry.fieldName || ''),
        order: i,
        data:  preview[i]?.data ?? [],
      })) as WidgetEntryWithData[];
    }

    if (widget.widgetType === 'DEVICE_LIST') {
      const base = sourceEntries.length
        ? sourceEntries.slice(0, mockCount)
        : Array.from({ length: mockCount }, (_, i) => ({
            color: this.atmosPreviewColors[i % this.atmosPreviewColors.length],
            label: `Sensor ${i + 1}`,
            unit:  '',
          }));
      const preview = buildPreviewEntries(base.map((e: any) => ({
        color: e.color || WIDGET_LIST_DEFAULT_COLOR,
        label: e.label || e.fieldName || `Sensor`,
        unit:  e.unit  ?? '',
      })));
      return (base as any[]).map((entry, i) => ({
        ...entry,
        color: entry.color || WIDGET_LIST_DEFAULT_COLOR,
        label: entry.label || entry.fieldName || `Sensor ${i + 1}`,
        order: i,
        data:  preview[i]?.data ?? [],
      })) as WidgetEntryWithData[];
    }

    // LINE / BAR
    const effectiveSource = sourceEntries.length
      ? sourceEntries
      : Array.from({ length: mockCount }, (_, i) => ({
          color:          this.atmosPreviewColors[i % this.atmosPreviewColors.length],
          label:          `Sensor ${i + 1}`,
          unit:           '',
          dataSourceType: 'DEVICE' as const,
        }));

    const previewSourceEntries = effectiveSource.slice(0, mockCount).map((entry: any, index) => ({
      ...entry,
      color: this.atmosPreviewColors[index % this.atmosPreviewColors.length],
      label: entry.label || entry.fieldName || `Sensor ${index + 1}`,
      order: index,
    }));

    const previewEntries = buildPreviewEntries(previewSourceEntries.map(entry => ({
      color:          entry.color          ?? undefined,
      label:          entry.label || entry.fieldName || undefined,
      unit:           entry.unit           ?? undefined,
      dataSourceType: entry.dataSourceType ?? undefined,
    })));

    return previewSourceEntries.map((entry, index) => ({
      ...entry,
      data: previewEntries[index]?.data ?? [],
    })) as WidgetEntryWithData[];
  }

  private getMockEntryCount(widget: Widget): number {
    const rules   = widget.entryRules ?? [];
    const entries = widget.entries   ?? [];
    if (rules.length > 0)   return Math.min(rules.length, 3);
    if (entries.length > 0) return Math.min(entries.length, 3);
    return 0;
  }

  private isDefaultNumberPreviewColor(color?: string | null): boolean {
    return !color || color === this.atmosPreviewColors[0] || color === WIDGET_SERIES_DEFAULT_COLORS[0];
  }

  private rebuildRenderableWidgetCache(): void {
    this.renderableWidgetCache = new Map(
      this.gridItems.map(item => [item.widget.id, this.buildRenderable(item.widget)])
    );
  }

  asRenderableWidget(item: DashboardGridItem): WidgetWithData {
    let renderable = this.renderableWidgetCache.get(item.widget.id);
    if (!renderable) {
      renderable = this.buildRenderable(item.widget);
      this.renderableWidgetCache.set(item.widget.id, renderable);
    }
    return renderable;
  }

  trackByWidgetId(_index: number, item: DashboardGridItem): string {
    return item.widget.id;
  }

  getSensorTypeLabels(widget: Widget): string[] {
    return this.sensorTypeLabelsCache.get(widget.id) ?? [];
  }

  private rebuildSensorTypeLabelsCache(): void {
    if (!this.sensorCatalog.length) {
      this.sensorTypeLabelsCache = new Map();
      return;
    }
    this.sensorTypeLabelsCache = new Map(
      this.gridItems.map(item => [item.widget.id, this.computeSensorTypeLabels(item.widget)])
    );
  }

  private computeSensorTypeLabels(widget: Widget): string[] {
    const seen = new Set<number>();
    const labels: string[] = [];
    const rules = widget.entryRules ?? [];
    const sources = [...(widget.entries ?? []), ...rules, ...rules.flatMap(rule => rule.entries ?? [])];
    for (const entry of sources) {
      if (entry.sensorType == null) continue;
      const id = Number(entry.sensorType);
      if (seen.has(id)) continue;
      seen.add(id);
      const cat = this.sensorCatalog.find(c => Number(c.id) === id);
      if (cat?.label) labels.push(cat.label);
    }
    return labels;
  }

  getFirstSource(widget: Widget): string {
    const s = (widget.entries ?? [])[0]?.dataSourceType;
    return s ? (this.SOURCE_LABELS[s] ?? s) : '—';
  }

  getWidgetTypeLabel(widget: Widget): string {
    return this.TYPE_LABELS[widget.widgetType] || widget.widgetType;
  }

  hasNoEntries(item: DashboardGridItem): boolean {
    // El mapa no necesita entradas: muestra los dispositivos de su area.
    if (item.widget.widgetType === 'MAP') return false;
    const hasRules   = (item.widget.entryRules ?? []).length > 0;
    const hasEntries = (item.widget.entries    ?? []).length > 0;
    if (hasRules || hasEntries) return false;
    if (this.mode === 'user') {
      return (this.widgetDataMap[item.widget.id] ?? []).length === 0;
    }
    return true;
  }

  hasNoDevice(item: DashboardGridItem): boolean {
    if (this.mode !== 'user') return false;
    const deviceEntries = (item.widget.entries ?? []).filter(entry => entry.dataSourceType === 'DEVICE');
    if (!deviceEntries.length) return false;
    return deviceEntries.every(entry => !entry.deviceId);
  }

  hasNoSyncedDevices(item: DashboardGridItem): boolean {
    if (this.mode !== 'user') return false;
    // El mapa se enseña igual (el contorno del área y su propio aviso de "sin dispositivos").
    if (item.widget.widgetType === 'MAP') return false;

    const deviceRules = (item.widget.entryRules ?? []).filter(rule => rule.dataSourceType === 'DEVICE');
    if (!deviceRules.length) return false;

    const deviceEntries = (item.widget.entries ?? []).filter(entry => entry.dataSourceType === 'DEVICE');
    return !deviceEntries.some(entry => !!entry.deviceId);
  }

  getNoDeviceIcon(item: DashboardGridItem): string {
    const entry = (item.widget.entries ?? [])[0];
    const key = entry?.fieldName || item.widget.title || '';
    const normalizedKey = key.toLowerCase();

    if (normalizedKey.includes('windspeed') || normalizedKey.includes('wind speed')) return 'air';
    if (normalizedKey.includes('winddirection') || normalizedKey.includes('wind direction')) return 'explore';

    return 'sensors';
  }
}
